import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { api, currency } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="plans-page">
      {/* Header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(242,106,33,0.10),transparent_55%)]" />
        <div className="relative max-w-[1300px] mx-auto px-5 sm:px-8 pt-14 md:pt-20 pb-14 text-center">
          <motion.p className="overline text-[#F26A21] mb-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            Annual maintenance plans
          </motion.p>
          <motion.h1
            className="font-heading font-bold text-5xl sm:text-6xl tracking-tight text-white leading-[1.05]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Complete appliance care.{" "}
            <span className="text-[#F26A21]">Zero hassle.</span>
          </motion.h1>
          <motion.p
            className="text-[#AEBECD] text-lg max-w-2xl mx-auto mt-6 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            Every plan is backed by brand-trained ASA technicians, genuine parts
            and fixed, transparent pricing. Upgrade or cancel anytime.
          </motion.p>
        </div>
      </section>

      {/* Cards */}
      <section className="max-w-[1300px] mx-auto px-5 sm:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {plans.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <div
                data-testid={`plan-card-${p.slug}`}
                className={`relative h-full flex flex-col rounded-3xl p-8 border transition-all duration-300 ${
                  p.popular
                    ? "border-[#F26A21] bg-[#122539] ring-1 ring-[#F26A21]/40 lg:-mt-4 lg:mb-4"
                    : "border-[#223851] bg-[#0F2236] hover:border-[#33506e]"
                }`}
              >
                {p.popular && (
                  <span className="self-start overline text-white bg-[#F26A21] px-3 py-1 rounded-full mb-3">
                    Most popular
                  </span>
                )}
                <h2 className="font-heading font-bold text-2xl text-white">{p.name}</h2>
                <p className="text-[#AEBECD] mt-2 text-sm min-h-[42px]">{p.tagline}</p>

                <div className="mt-6 flex items-end gap-1">
                  <span className="font-heading font-extrabold text-5xl text-white">₹{currency(p.price)}</span>
                  <span className="text-[#6B7E92] mb-1.5">/{p.period}</span>
                </div>
                <p className="overline text-[#6B7E92] mt-3">
                  {p.appliances_covered} · {p.visits_per_year}
                </p>

                <ul className="mt-7 space-y-3 flex-1">
                  {p.features.map((f, fi) => (
                    <li key={fi} className="flex gap-3 text-[#D3DEE8] text-sm">
                      <Check size={17} className="text-[#F26A21] shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>

                {p.warranty?.length > 0 && (
                  <div className="mt-6 rounded-2xl bg-[#0C1C2E] border border-[#223851] p-4">
                    <p className="overline text-[#6B7E92] mb-2">Warranty & timeline</p>
                    <ul className="space-y-1.5">
                      {p.warranty.map((w, wi) => (
                        <li key={wi} className="text-xs text-[#AEBECD] leading-relaxed">• {w}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <Link
                  to={`/get-id?plan=${p.slug}`}
                  data-testid={`plan-subscribe-${p.slug}`}
                  className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-semibold transition-colors duration-300 group ${
                    p.popular
                      ? "bg-[#F26A21] text-white hover:bg-[#DE5C15]"
                      : "bg-white/5 text-white border border-[#33506e] hover:bg-[#F26A21] hover:border-[#F26A21]"
                  }`}
                >
                  Choose {p.name}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* One-time services */}
      {services.length > 0 && (
        <section className="max-w-[1300px] mx-auto px-5 sm:px-8 pb-16">
          <Reveal>
            <div className="text-center mb-10">
              <p className="overline text-[#F26A21] mb-3">No subscription needed</p>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-white">
                One-time services, flat rates.
              </h2>
              <p className="text-[#AEBECD] mt-4 max-w-xl mx-auto">
                Just need a quick fix or an install? Book a single visit at a
                fixed price — no plan required.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08}>
                <div
                  data-testid={`service-card-${s.slug}`}
                  className="h-full flex flex-col rounded-3xl bg-[#122539] border border-[#223851] p-8 hover:border-[#F26A21] transition-colors duration-300"
                >
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading font-extrabold text-4xl text-[#F26A21]">
                      ₹{currency(s.price)}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-white mt-4">{s.name}</h3>
                  <p className="text-[#AEBECD] mt-2 flex-1 leading-relaxed">{s.description}</p>
                  <Link
                    to={`/enquire?service=${s.slug}`}
                    data-testid={`service-book-${s.slug}`}
                    className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold bg-white/5 text-white border border-[#33506e] hover:bg-[#F26A21] hover:border-[#F26A21] transition-colors duration-300 group"
                  >
                    Book this service
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
